import { hrmsGet, hrmsPatch, hrmsPut } from "../api/api";
import sessionDB from "../utils/sessionDB";


const BASE = "/emp-onboarding-requests";

/** Search reads; the update and the status flip are writes. */
const READ = "HR33S";
const WRITE = "HR33U";

function query(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  });
  return search.toString();
}

/** Rows live under data.content when paged, or data itself when not. */
function rowsOf(response) {
  const data = response?.data ?? {};
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.content)) return data.content;
  return [];
}

/** The paged envelope, in the shape a list screen reads. */
function pageOf(response) {
  const data = response?.data ?? {};
  const rows = rowsOf(response);
  return { rows, totalCount: data.totalElements ?? rows.length };
}

/**
 * A write that answered 2xx with an error envelope.
 *
 * The transport rejects only on a non-OK status, and a duplicate onboarding
 * comes back as 208 with errorCode DUPLICATE_ENTRY — a success as far as fetch
 * is concerned. Left alone it would resolve, and the screen would report a
 * request onboarded that the server refused to touch.
 *
 * An errorCode in the body is a failure whatever the status line says, thrown
 * in the same shape parseResponse throws so the callers need no special case.
 */
function assertWritten(envelope) {
  const errorCode = envelope?.errorCode;
  if (!errorCode) return envelope;

  const error = new Error(envelope.message || errorCode);
  error.errorCode = errorCode;
  error.data = envelope.data;
  throw error;
}

/* -------------------------------------------------------------------- read */

/**
 * GET /saas/hrms/emp-onboarding-requests/search — HR33S.
 * Every request for the tenant, newest first.
 */
export async function fetchOnboardingRequests(params = {}) {
  const response = await hrmsGet(`${BASE}/search?&sortDir=DESC${query({ page: 0, size: 10, ...params })}`, READ);
  return pageOf(response);
}

/** GET /saas/hrms/emp-onboarding-requests/search/status — HR33S. */
export async function fetchOnboardingRequestsByStatus(requestStatus, params = {}) {
  const response = await hrmsGet(
    `${BASE}/search/status?${query({ requestStatus, page: 0, size: 10, ...params })}`,
    READ
  );
  return pageOf(response);
}

/** GET /saas/hrms/emp-onboarding-requests/search/name — HR33S. */
export async function searchOnboardingRequestsByName(name, params = {}) {
  const response = await hrmsGet(
    `${BASE}/search/name?${query({ name, page: 0, size: 10, ...params })}`,
    READ
  );
  return pageOf(response);
}

/** GET /saas/hrms/emp-onboarding-requests/search/work-location — HR33S. */
export async function fetchOnboardingRequestsByWorkLocation(workLocation, params = {}) {
  const response = await hrmsGet(
    `${BASE}/search/work-location?${query({ workLocation, page: 0, size: 10, ...params })}`,
    READ
  );
  return pageOf(response);
}

/** GET /saas/hrms/emp-onboarding-requests/{requestId} — HR33S. */
export async function getOnboardingRequest(requestId) {
  const response = await hrmsGet(`${BASE}/${encodeURIComponent(requestId)}`, READ);
  return response?.data ?? null;
}

/** GET /saas/hrms/emp-onboarding-progress/{empId}. */
export async function fetchOnboardingProgress(empId) {
  const response = await hrmsGet(`/emp-onboarding-progress/${encodeURIComponent(empId)}`, READ);
  return response?.data ?? null;
}

/* ----------------------------------------------------------- org lookups */

/**
 * The tenant every lookup is scoped to — the signed-in one, never a literal.
 * A hardcoded id would read another tenant's roles into this one's form.
 */
function tenantId() {
  return String(sessionDB.getTenantId() ?? "").trim();
}

/** Lookups answer a bare array, or an envelope; both are read the same way. */
function lookupRows(response) {
  if (Array.isArray(response)) return response;
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  return [];
}

/**
 * One option out of a lookup row, whatever the row is.
 *
 * These masters do not answer in one shape: a list of plain strings, or rows
 * keyed empType / typeName / name / value, or an id beside a label. Rather
 * than name every spelling, this takes the first readable string it can —
 * a preferred key if the row has one, otherwise the first string field on it.
 * A row that yields nothing readable is dropped instead of becoming a blank
 * line in a dropdown.
 */
function toOption(row, preferredKeys = []) {
  if (row == null) return null;

  if (typeof row === "string" || typeof row === "number") {
    const text = String(row).trim();
    return text ? { value: text, label: text } : null;
  }

  if (typeof row !== "object") return null;

  const named = preferredKeys
    .concat(["name", "label", "value", "code", "description"])
    .map((key) => row[key])
    .find((value) => typeof value === "string" && value.trim() !== "");

  const text =
    named ??
    Object.values(row).find((value) => typeof value === "string" && value.trim() !== "");

  return text ? { value: String(text).trim(), label: String(text).trim() } : null;
}

/**
 * GET /saas/hrms/org/roles?tenantId= — HR20A.
 * The service code and the shape are the onboarding screens' own, so the two
 * places that read roles read the same thing.
 */
export async function fetchRoles() {
  const response = await hrmsGet(`/org/roles?${query({ tenantId: tenantId() })}`, "HR20A");
  return lookupRows(response)
    .map((row) => ({
      id: row?.roleId ?? row?.id ?? row?.value,
      name: row?.roleName ?? row?.name ?? row?.label ?? row?.value,
    }))
    .filter((row) => row.id != null && row.name);
}

/** GET /saas/hrms/org/canonical_roles?tenantId= — HR20A. */
export async function fetchCanonicalRoles() {
  const response = await hrmsGet(
    `/org/canonical_roles?${query({ tenantId: tenantId() })}`,
    "HR20A"
  );
  return lookupRows(response)
    .map((row) => ({
      id: row?.canonicalRoleId ?? row?.roleId ?? row?.id ?? row?.value,
      name: row?.canonicalRoleName ?? row?.roleName ?? row?.name ?? row?.label ?? row?.value,
    }))
    .filter((row) => row.name);
}


/**
 * GET /saas/hrms/org/departments?tenantId= — HR20A.
 *
 * The department the new employee is filed under. Read as {id, name} like the
 * other masters: the employee record stores the id, so the name is only ever
 * the label on it.
 */
export async function fetchDepartments() {
  const response = await hrmsGet(`/org/departments?${query({ tenantId: tenantId() })}`, "HR20A");
  return lookupRows(response)
    .map((row) => ({
      id: row?.departmentId ?? row?.deptId ?? row?.id ?? row?.value,
      name: row?.departmentName ?? row?.deptName ?? row?.name ?? row?.label ?? row?.value,
    }))
    .filter((row) => row.id != null && row.name);
}

/**
 * GET /saas/hrms/org/work-locations?tenantId= — HR20A.
 *
 * The tenant's own locations, assigned by HR when approving the request.
 */
export async function fetchWorkLocations() {
  const response = await hrmsGet(
    `/org/work-locations?${query({ tenantId: tenantId() })}`,
    "HR20A"
  );
  return lookupRows(response)
    .map((row) => ({
      id: row?.workLocationId ?? row?.locationId ?? row?.id ?? row?.value,
      name:
        row?.workLocationName ?? row?.locationName ?? row?.name ?? row?.label ?? row?.value,
    }))
    .filter((row) => row.id != null && row.name);
}
/**
 * GET /saas/hrms/org/emp-type?tenantId= — HR20A.
 *
 * Employment types are stored as the value the employee record carries, so the
 * option's value IS its label — there is no id to send instead.
 */
export async function fetchEmpTypes() {
  const response = await hrmsGet(`/org/emp-type?${query({ tenantId: tenantId() })}`, "HR20A");
  return lookupRows(response)
    .map((row) => toOption(row, ["empType", "typeName", "empTypeName", "type"]))
    .filter(Boolean);
}

/** GET /saas/hrms/org/emp-status?tenantId= — HR20A. */
export async function fetchEmpStatuses() {
  const response = await hrmsGet(`/org/emp-status?${query({ tenantId: tenantId() })}`, "HR20A");
  return lookupRows(response)
    .map((row) => toOption(row, ["empStatus", "statusName", "empStatusName", "status"]))
    .filter(Boolean);
}

/**
 * GET /saas/hrms/org/states?tenantId= — HR20A.
 *
 * The states a work address may be in. The address arrives as free text from
 * a form the candidate filled with no help, so it is picked from this list on
 * review rather than trusted as typed.
 */
export async function fetchStates() {
  const response = await hrmsGet(`/org/states?${query({ tenantId: tenantId() })}`, "HR20A");
  return lookupRows(response)
    .map((row) => ({
      id: row?.stateId ?? row?.id ?? row?.value,
      name: row?.stateName ?? row?.name ?? row?.label ?? row?.value,
    }))
    .filter((row) => row.name);
}

/**
 * GET /saas/hrms/org/cities/by-state/{stateId}?tenantId= — HR20A.
 *
 * Districts belong to one state, so this is read per state and not up front:
 * without a state chosen there is no list to show, and an id from another
 * state would offer districts the address cannot be in.
 */
export async function fetchCitiesByState(stateId) {
  if (stateId == null || String(stateId).trim() === "") return [];

  const response = await hrmsGet(
    `/org/cities/by-state/${encodeURIComponent(stateId)}?${query({ tenantId: tenantId() })}`,
    "HR20A"
  );
  return lookupRows(response)
    .map((row) => ({
      id: row?.cityId ?? row?.id ?? row?.value,
      name: row?.cityName ?? row?.name ?? row?.label ?? row?.value,
    }))
    .filter((row) => row.name);
}

/**
 * GET /saas/hrms/emp_shift/search — HR19S.
 *
 * The shifts HR picks from. The start and end times come back with each one
 * and are shown beside its name: two shifts often differ only by their hours,
 * so the name alone would not tell them apart.
 */
export async function fetchShifts() {
  const response = await hrmsGet(`/emp_shift/search?${query({ page: 0, size: 10 })}`, "HR19S");
  return lookupRows(response)
    .map((row) => ({
      id: row?.shiftId ?? row?.id,
      name: row?.shiftName ?? row?.name ?? row?.shiftCode ?? "",
      startTime: row?.shiftStartTime ?? "",
      endTime: row?.shiftEndTime ?? "",
    }))
    .filter((row) => row.id != null);
}

/* ------------------------------------------------------------------- write */

/**
 * PUT /saas/hrms/emp-onboarding-requests/update/{requestId} — HR33U.
 *
 * The request as HR completed it. Sent whole, the way it was read, so a field
 * the reviewer did not touch travels back unchanged.
 */
export async function updateOnboardingRequest(requestId, payload) {
  const response = assertWritten(
    await hrmsPut(
      `${BASE}/update/${encodeURIComponent(requestId)}`,
      payload,
      WRITE
    )
  );
  return response?.data ?? null;
}

/**
 * PATCH /saas/hrms/emp-onboarding-requests/update/status/{requestId} — HR33U.
 *
 * A status on its own, carrying nothing else. This is how a request is
 * REJECTED — from the detail screen, and from the list for a request that has
 * no detail to open yet.
 *
 * Approving does NOT come through here: onboarding happens in the update
 * endpoint, which takes the completed request and its new status together.
 */
export async function updateOnboardingRequestStatus(requestId, requestStatus) {
  const response = assertWritten(
    await hrmsPatch(
      `${BASE}/update/status/${encodeURIComponent(requestId)}`,
      { requestStatus },
      WRITE
    )
  );
  return response?.data ?? null;
}

export const onboardingRequestService = {
  fetchOnboardingRequests,
  fetchOnboardingRequestsByStatus,
  searchOnboardingRequestsByName,
  fetchOnboardingRequestsByWorkLocation,
  getOnboardingRequest,
  fetchRoles,
  fetchCanonicalRoles,
  fetchDepartments,
  fetchWorkLocations,
  fetchEmpTypes,
  fetchEmpStatuses,
  fetchStates,
  fetchCitiesByState,
  fetchShifts,
  fetchOnboardingProgress,
  updateOnboardingRequest,
  updateOnboardingRequestStatus,
};

export default onboardingRequestService;
